import { GraphQLSchema, parse } from 'graphql';
import { pick } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import { useCallback, useMemo } from 'react';

import { getRootTypeNames } from 'src/lib/graphql/graphqlHelpers';

import {
  getActiveFragment,
  getDocumentDefinitions,
} from '../helpers/graphqlParseHelpers';
import {
  getActiveOperation,
  getGraphqlInactiveRanges,
} from '../helpers/monacoHelpers';

import { useDerivedEditorValue } from './useDerivedEditorValue';
import { useEditorModel } from './useEditorModel';
import { useInlineClassRangesEffect } from './useInlineClassRangesEffect';

interface DefinitionDetails {
  isValid: boolean;
  isSubscription: boolean;
  name: string | null | undefined;
  activePartsOfDocument: string | undefined;
  activePartsOfDocumentWithUnusedVariables: string | undefined;
  activeRanges: monacoEditor.Range[];
}

const getIsOperationValid = (
  activeOperation: ReturnType<typeof getActiveOperation>,
) => {
  if (!activeOperation) return false;

  try {
    parse(activeOperation.activePartsOfDocument);
    return true;
  } catch (e) {
    // Failed to parse active document string;
    return false;
  }
};
const useIsOperationValid = (
  activeOperation: ReturnType<typeof getActiveOperation>,
) => useMemo(() => getIsOperationValid(activeOperation), [activeOperation]);

export const useActiveDefinitionDetails = (
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  schema: GraphQLSchema,
): DefinitionDetails | undefined => {
  const model = useEditorModel(editor);

  const { operationDefinitions, fragmentDefinitions, commentRanges } =
    useDerivedEditorValue({
      editor,
      getValue: useCallback(
        (editorValue: string) => {
          if (!editorValue) return;
          return pick(
            getDocumentDefinitions(editorValue, getRootTypeNames(schema)),
            ['fragmentDefinitions', 'operationDefinitions', 'commentRanges'],
          );
        },
        [schema],
      ),
      withPosition: false,
    }) ?? {};

  const activeOperation = useDerivedEditorValue({
    editor,
    getValue: useCallback(
      (_, editorPosition: monacoEditor.Position | undefined) =>
        getActiveOperation(
          editorPosition,
          model,
          fragmentDefinitions ?? [],
          operationDefinitions ?? [],
          commentRanges ?? [],
          schema,
        ),
      [fragmentDefinitions, model, operationDefinitions, commentRanges, schema],
    ),
    withPosition: true,
  });

  const activeFragment = useDerivedEditorValue({
    editor,
    getValue: useCallback(
      (_, editorPosition: monacoEditor.Position | undefined) =>
        getActiveFragment(editorPosition, fragmentDefinitions ?? []),
      [fragmentDefinitions],
    ),
    withPosition: true,
  });

  const isOperationValid = useIsOperationValid(activeOperation);

  const inactiveRanges = useMemo(
    () =>
      getGraphqlInactiveRanges({
        model,
        activeOperation,
        activeFragment,
        fragmentDefinitions,
      }),
    [activeOperation, activeFragment, fragmentDefinitions, model],
  );

  useInlineClassRangesEffect({
    className: 'opacity-50',
    editor,
    ranges: inactiveRanges,
    skip: !(operationDefinitions && fragmentDefinitions),
  });

  return useMemo(
    () =>
      activeOperation
        ? {
            isValid: isOperationValid,
            activePartsOfDocument: activeOperation.activePartsOfDocument,
            activePartsOfDocumentWithUnusedVariables:
              activeOperation.activePartsOfDocumentWithUnusedVariables,
            name: activeOperation.name,
            activeRanges: activeOperation.operationActiveRanges,
            isSubscription: activeOperation.isSubscription,
          }
        : undefined,
    [isOperationValid, activeOperation],
  );
};

export const useDefinitionDetails = (
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  schema: GraphQLSchema,
  position: monacoEditor.Position,
): Partial<DefinitionDetails> => {
  const model = useEditorModel(editor);

  const { operationDefinitions, fragmentDefinitions, commentRanges } =
    useDerivedEditorValue({
      editor,
      getValue: useCallback(
        (editorValue: string) => {
          if (!editorValue) return;
          return pick(
            getDocumentDefinitions(editorValue, getRootTypeNames(schema)),
            ['fragmentDefinitions', 'operationDefinitions', 'commentRanges'],
          );
        },
        [schema],
      ),
      withPosition: false,
    }) ?? {};

  return useMemo(() => {
    let activeOperation:
      | ReturnType<typeof getActiveOperation>
      | null
      | undefined;
    function getActiveOperationCached() {
      if (activeOperation === undefined) {
        activeOperation = getActiveOperation(
          position,
          model,
          fragmentDefinitions ?? [],
          operationDefinitions ?? [],
          commentRanges ?? [],
          schema,
        );
      }
      return activeOperation ?? null;
    }

    let isValid: boolean | undefined;
    return {
      get isValid(): boolean {
        if (typeof isValid !== 'boolean') {
          isValid = getIsOperationValid(
            getActiveOperationCached() ?? undefined,
          );
        }

        return isValid;
      },
      get activePartsOfDocument() {
        return getActiveOperationCached()?.activePartsOfDocument;
      },
      get activePartsOfDocumentWithUnusedVariables() {
        return getActiveOperationCached()
          ?.activePartsOfDocumentWithUnusedVariables;
      },
      get name() {
        return getActiveOperationCached()?.name;
      },
      get activeRanges() {
        return getActiveOperationCached()?.activeRanges;
      },
      get isSubscription() {
        return getActiveOperationCached()?.isSubscription;
      },
    };
  }, [
    commentRanges,
    fragmentDefinitions,
    model,
    operationDefinitions,
    position,
    schema,
  ]);
};
